import Cookies from "js-cookie";
import {
  clearStoredSession,
  readStoredSession,
  writeStoredSession,
} from "@/core/auth/session-storage";
import type { ApiResponse } from "@/core/data/api-response";
import { navPaths } from "../constants";
import { appConfig } from "../config";

const REFRESH_PATH = "/api/v1/auth/refresh";

/** 401 on these calls must not trigger refresh (avoids infinite loops). */
const skipRefreshOn401 = (path: string) =>
  path.includes("/api/v1/auth/refresh") ||
  path.includes("/api/v1/auth/login");

function cloneBodyForRetry(body: BodyInit): BodyInit {
  if (typeof body === "string") return body;
  if (body instanceof URLSearchParams) return new URLSearchParams(body);
  if (body instanceof FormData) {
    const copy = new FormData();
    body.forEach((value, key) => {
      copy.append(key, value);
    });
    return copy;
  }
  if (body instanceof Blob) return body.slice();
  if (body instanceof ArrayBuffer) return body.slice(0);
  return body;
}

function cloneInitForRetry(init?: RequestInit): RequestInit | undefined {
  if (!init?.body) return init ? { ...init } : init;
  return { ...init, body: cloneBodyForRetry(init.body) };
}

function authFetch(url: string, init?: RequestInit): Promise<Response> {
  const token = Cookies.get("accessToken");
  const isFormData = init?.body instanceof FormData;
  return fetch(`${appConfig.apiBaseUrl}${url}`, {
    ...init,
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });
}

let inflightRefresh: Promise<boolean> | null = null;
let loginRedirectScheduled = false;

function redirectToLogin() {
  if (loginRedirectScheduled) return;
  loginRedirectScheduled = true;
  clearStoredSession();
  window.location.assign(navPaths.login);
}

async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = Cookies.get("refreshToken");
  if (!refreshToken) return false;

  try {
    const res = await fetch(`${appConfig.apiBaseUrl}${REFRESH_PATH}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    const body = (await res.json()) as ApiResponse<{
      access_token: string;
    }>;

    if (!res.ok || !body.success || !body.data?.access_token) {
      return false;
    }

    const session = readStoredSession();
    if (!session) return false;

    writeStoredSession({
      ...session,
      accessToken: body.data.access_token,
    });
    return true;
  } catch {
    return false;
  }
}

function sharedRefresh(): Promise<boolean> {
  if (!inflightRefresh) {
    inflightRefresh = refreshAccessToken().finally(() => {
      inflightRefresh = null;
    });
  }
  return inflightRefresh;
}

export const fetchWithAuth = async (
  url: string,
  init?: RequestInit,
): Promise<Response> => {
  const response = await authFetch(url, init);

  if (response.status !== 401 || skipRefreshOn401(url)) {
    return response;
  }

  if (!Cookies.get("refreshToken")) {
    redirectToLogin();
    return response;
  }

  const refreshed = await sharedRefresh();
  if (!refreshed) {
    redirectToLogin();
    return response;
  }

  return authFetch(url, cloneInitForRetry(init));
};
